import { Link } from "react-router-dom";
import { Section, Eyebrow, DisclaimerNote } from "@/components/Primitives";

const credentials = [
  "Hypnotherapy",
  "Neuro-Linguistic Programming (NLP)",
  "National Guild of Hypnotists (NGH)",
  "American Council of Hypnotist Examiners (ACHE)",
  "Positive Mind Management (PMMCC)",
  "Adversarial Resistance approaches",
  "Enneagram-based personal development",
];

const working = [
  { icon: "fa-wind", t: "Calm", d: "The goal is understanding, not pressure." },
  { icon: "fa-handshake", t: "Respectful", d: "Clients are active participants in their own transformation." },
  { icon: "fa-magnifying-glass", t: "Curious", d: "Not only \u201chow do we make this stop?\u201d but \u201cwhat's happening underneath it?\u201d" },
  { icon: "fa-compass", t: "Values-centered", d: "Change is connected to who you want to become and what matters most." },
  { icon: "fa-list-check", t: "Practical", d: "Insight matters, but the goal is different responses in real life." },
];

export default function About() {
  return (
    <div className="pb-8">
      {/* Short intro + photo */}
      <Section className="pt-14 sm:pt-20">
        <div className="grid lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-3">
            <Eyebrow>Meet Jonathan</Eyebrow>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-stone-900 leading-[1.08] mt-4">
              Helping people understand the patterns they've struggled to change
            </h1>
            <p className="text-lg text-stone-600 leading-relaxed mt-6">
              For many people, the hardest part of change isn't knowing what they want to stop doing —
              it's understanding <em>why</em> the same behavior, reaction, or pattern keeps returning,
              even when they sincerely want something different. Jonathan's work is centered on
              exploring those deeper patterns with curiosity, structure, and compassion — helping
              clients move from automatic reaction, to greater awareness, to more intentional response.
            </p>
            <Link
              to="/consultation"
              data-testid="about-consultation-cta"
              className="inline-flex btn-terracotta rounded-full px-7 py-4 text-sm font-semibold mt-8"
            >
              Schedule a private consultation
            </Link>
          </div>
          <div className="lg:col-span-2">
            <div className="rounded-[2rem] overflow-hidden shadow-2xl border border-stone-200 max-w-sm mx-auto">
              <img
                src="/jonathan.jpg"
                alt="Jonathan Don Manning, Certified Clinical Hypnotherapist"
                className="w-full aspect-[4/5] object-cover"
                data-testid="jonathan-photo"
              />
            </div>
            <p className="text-center text-xs text-stone-500 mt-4">
              Jonathan Don Manning · Founder, <span className="font-semibold text-stone-700">TENET Transformation</span>
            </p>
          </div>
        </div>
      </Section>

      {/* Brand statement */}
      <Section className="mt-20">
        <div className="rounded-[2rem] bg-[#1F3D2E] p-10 sm:p-14 text-center">
          <p className="font-serif-display text-3xl sm:text-4xl text-amber-50 max-w-3xl mx-auto leading-tight">
            "Lasting change often requires more than fighting the behavior you can see."
          </p>
        </div>
      </Section>

      {/* A different approach */}
      <Section className="mt-24">
        <div className="max-w-3xl">
          <Eyebrow>A different approach to change</Eyebrow>
          <h2 className="font-playfair text-3xl sm:text-4xl text-stone-900 mt-3">
            Sometimes the visible behavior is only one part of a larger pattern
          </h2>
          <p className="text-stone-600 leading-relaxed mt-5">
            Jonathan founded TENET around a simple idea: lasting change often requires more than
            fighting the behavior you can see. Stress, emotional triggers, learned responses, beliefs,
            identity, routines, and subconscious associations can all influence how we respond in
            difficult moments.
          </p>
          <p className="text-stone-600 leading-relaxed mt-4">
            TENET is designed to help clients slow that process down, understand it more clearly, and
            begin developing different responses. Depending on the individual and their goals, the work
            may incorporate hypnotherapy, guided reflection, reframing, pattern awareness, and
            visualization. The emphasis is always on moving from
            <strong> automatic reaction → greater awareness → intentional response.</strong>
          </p>
        </div>
      </Section>

      {/* Faith / science / person-centered */}
      <Section className="mt-20">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            ["fa-dove", "Faith-informed", "For clients who value faith, those beliefs can be a source of meaning, identity, and direction — never a requirement."],
            ["fa-flask", "Science-informed", "Change isn't reduced to \u201chaving more faith\u201d or trying harder; it draws on practical approaches to habit and subconscious patterns."],
            ["fa-user", "Person-centered", "You're invited to use your values as part of understanding where you want your life to go."],
          ].map(([icon, t, d]) => (
            <div key={t} className="bg-white rounded-2xl border border-stone-200 p-7">
              <div className="grid place-items-center h-12 w-12 rounded-xl bg-[#1F3D2E]/10">
                <i className={`fa-solid ${icon} text-[#1F3D2E]`} />
              </div>
              <h3 className="font-playfair text-xl text-stone-900 mt-5">{t}</h3>
              <p className="text-stone-600 text-sm mt-2 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Why Jonathan does this work */}
      <Section className="mt-24">
        <div className="bg-[#F0EBE0] rounded-[2rem] p-8 sm:p-12 border border-stone-200">
          <div className="max-w-3xl">
            <Eyebrow>Why Jonathan does this work</Eyebrow>
            <h2 className="font-playfair text-3xl text-stone-900 mt-3">
              The gap between "I know this is hurting me" and "why do I keep going back to it?"
            </h2>
            <p className="text-stone-600 leading-relaxed mt-5">
              Jonathan has spent years studying why people repeat patterns they genuinely want to
              change. That question — the gap between knowing and doing — became central to the
              development of TENET. His work focuses especially on people who have already tried
              willpower, promises, accountability, self-discipline, or repeated fresh starts, and still
              find themselves returning to familiar patterns.
            </p>
            <p className="text-stone-600 leading-relaxed mt-4">
              He doesn't approach that struggle as evidence that someone is weak or incapable of change.
              Instead, he helps clients get curious about what the pattern may be doing, when it appears,
              what triggers it, and what would need to change for a different response to become more
              available.
            </p>
          </div>
        </div>
      </Section>

      {/* Sacred Flame specialty */}
      <Section className="mt-24">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <Eyebrow>Specialized work with unwanted sexual patterns</Eyebrow>
            <h2 className="font-playfair text-3xl text-stone-900 mt-3">The Sacred Flame pathway</h2>
            <p className="text-stone-600 leading-relaxed mt-5">
              One area of Jonathan's work is helping individuals who want to change unwanted pornography
              or sexual behavior. Through TENET's Sacred Flame pathway, clients explore the patterns,
              triggers, beliefs, emotional responses, and circumstances that may be contributing to
              those behaviors.
            </p>
            <p className="text-stone-600 leading-relaxed mt-4">
              The goal is not simply to suppress unwanted behavior — it's to better understand the
              pattern, strengthen alignment with your values, and develop healthier, more intentional
              responses. Sacred Flame is one specialized pathway within the broader TENET approach.
            </p>
            <Link
              to="/sacred-flame"
              data-testid="about-sacred-flame-cta"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#1F3D2E] mt-6 hover:gap-3 transition-all"
            >
              Explore Sacred Flame <i className="fa-solid fa-fire text-xs" />
            </Link>
          </div>
          <div className="rounded-2xl overflow-hidden border border-stone-200 shadow-lg">
            <img src="/brand-crossroads.png" alt="A coastal path toward a lighthouse" className="w-full h-72 object-cover" />
          </div>
        </div>
      </Section>

      {/* Training & Professional Development */}
      <Section className="mt-24">
        <div className="max-w-3xl">
          <Eyebrow>Training &amp; professional development</Eyebrow>
          <h2 className="font-playfair text-3xl text-stone-900 mt-3">Extensive study — in service of the person in front of him</h2>
          <p className="text-stone-600 leading-relaxed mt-5">
            Jonathan has pursued extensive training in hypnotherapy, NLP, behavioral change, subconscious
            pattern work, and personal development. His education and certifications are associated with
            organizations and methodologies including:
          </p>
        </div>
        <div className="flex flex-wrap gap-3 mt-6" data-testid="credentials-list">
          {credentials.map((c) => (
            <span key={c} className="bg-white border border-stone-200 rounded-full px-4 py-2 text-sm text-stone-700">
              <i className="fa-solid fa-certificate text-[#8B6F3D] mr-2" />{c}
            </span>
          ))}
        </div>
        <p className="text-stone-500 text-sm leading-relaxed mt-6 max-w-3xl">
          These tools inform Jonathan's work, but they don't define the relationship he has with clients.
          The most important part of the process is understanding the individual in front of him — their
          goals, values, experiences, patterns, and readiness for change.
        </p>
      </Section>

      {/* What working with Jonathan is like */}
      <Section className="mt-24">
        <div className="max-w-2xl">
          <Eyebrow>What working with Jonathan is like</Eyebrow>
          <h2 className="font-playfair text-3xl text-stone-900 mt-3">A calm, respectful, curious process</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5 mt-8">
          {working.map((w) => (
            <div key={w.t} className="bg-white rounded-2xl border border-stone-200 p-6">
              <i className={`fa-solid ${w.icon} text-[#1F3D2E] text-lg`} />
              <h3 className="font-playfair text-lg text-stone-900 mt-3">{w.t}</h3>
              <p className="text-stone-600 text-sm mt-1.5 leading-relaxed">{w.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Scope of care + final CTA */}
      <Section className="mt-24">
        <div className="bg-white rounded-[2rem] border border-stone-200 p-8 sm:p-12">
          <h2 className="font-playfair text-2xl text-stone-900">You don't have to know exactly what comes next</h2>
          <p className="text-stone-600 leading-relaxed mt-4 max-w-3xl">
            You may already know what you want to change. You may simply be unsure why changing it has
            been so difficult. That's often a good place to begin.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mt-7">
            <Link
              to="/assessments/tenet-readiness"
              data-testid="about-assessment-cta"
              className="btn-terracotta rounded-full px-7 py-4 text-sm font-semibold text-center"
            >
              Take the Transformation Readiness Assessment
            </Link>
            <Link
              to="/consultation"
              data-testid="about-consultation-cta-2"
              className="rounded-full px-7 py-4 text-sm font-semibold text-center border border-stone-300 text-stone-800 hover:bg-[#FBF7F1] transition-colors"
            >
              Schedule a private consultation
            </Link>
          </div>
          <DisclaimerNote
            className="mt-8"
            text="TENET is a personal transformation and hypnotherapy practice. Jonathan does not assume every unwanted behavior is caused by trauma, nor is hypnotherapy a replacement for medical, psychiatric, or licensed mental-health treatment when those are appropriate. Some individuals may benefit from working with other qualified professionals alongside — or instead of — hypnotherapy. The goal is to help each person identify the support that best fits their situation."
          />
        </div>
      </Section>
    </div>
  );
}
